// Tool selection quizzes: questions, scoring, and URL helpers shared by the pages and the tests.
//
// Each option gives points to one or more results. The result with the most points wins; ties go to
// the result listed first in `order`, which puts the simpler, cheaper option first (and Sigma, which
// Greyfield sells, last). Reasons come from the answers that gave the winner the most points.

export const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

/** Points an option must give the winner before its reason is shown. */
export const REASON_MIN_POINTS = 2;
export const MAX_REASONS = 3;

export const ECOMMERCE_QUIZ = Object.freeze({
  id: 'ecommerce',
  path: '/tools/ecommerce-analytics-tool-quiz',
  order: ['shopify-reports', 'profit-app', 'attribution-tool', 'retention-tool', 'amazon-tool', 'managed-warehouse', 'custom-build'],
  results: {
    'shopify-reports': {
      name: 'Start with Shopify’s own reports',
      shortName: 'Shopify reports',
      summary: 'Shopify Analytics is included in every plan, has cohorts and profit reports from your unit costs, and needs no setup. Spend on growth until you hit a question it cannot answer.',
      revisit: 'Revisit when another channel becomes a real share of revenue, or when profit has to include fees and ad spend.',
      compare: ['shopify-analytics-vs-custom-data-warehouse', 'lifetimely-vs-custom-data-warehouse', 'beprofit-vs-custom-data-warehouse'],
      fallbackReasons: ['Shopify’s reports cost nothing extra and cover sales, cohorts, and product profit from your unit costs.', 'Nothing in your answers needs data from outside the store yet.'],
    },
    'profit-app': {
      name: 'A Shopify profit app, such as Lifetimely, BeProfit, or TrueProfit',
      shortName: 'a profit app',
      summary: 'These apps calculate profit per order from revenue, product costs, shipping, fees, and ad spend, and set up in hours rather than months. Lifetimely and BeProfit have free plans; TrueProfit also covers TikTok Shop.',
      revisit: 'Revisit when Amazon or wholesale matter, or when finance needs numbers that tie to the books.',
      compare: ['lifetimely-vs-custom-data-warehouse', 'beprofit-vs-custom-data-warehouse', 'trueprofit-vs-custom-data-warehouse'],
      fallbackReasons: ['A profit app gets you contribution profit per order without a project.', 'Your answers fit a ready-made profit model rather than your own definitions.'],
    },
    'attribution-tool': {
      name: 'An attribution tool, such as Triple Whale or Northbeam',
      shortName: 'an attribution tool',
      summary: 'If the question is which ads are working, a tool with its own pixel and attribution models answers it faster and cheaper than a warehouse project. Triple Whale is Shopify-first; Northbeam goes deeper on measurement for heavy paid media spend.',
      revisit: 'Revisit when finance needs margin by channel, or when Amazon, TikTok Shop, or wholesale become a large share of revenue.',
      compare: ['triple-whale-vs-custom-data-warehouse', 'northbeam-vs-custom-data-warehouse'],
      fallbackReasons: ['Attribution tools ship a pixel and models a warehouse build does not.', 'Your answers point to marketing questions more than accounting ones.'],
    },
    'retention-tool': {
      name: 'A retention analytics tool, such as Peel',
      shortName: 'a retention tool',
      summary: 'Peel ships cohort metrics, LTV, customer journeys, and RFM for Shopify and Amazon out of the box, and includes Snowflake data access on every plan. Lifetimely is a lighter option for a single Shopify store.',
      revisit: 'Revisit when TikTok Shop, wholesale, or ERP data matter, or when finance needs full contribution margin.',
      compare: ['peel-vs-custom-data-warehouse', 'lifetimely-vs-custom-data-warehouse'],
      fallbackReasons: ['Cohort and LTV reports come ready-made instead of being modeled from scratch.', 'Your answers fit a vendor’s definitions of repeat purchase and LTV.'],
    },
    'amazon-tool': {
      name: 'An Amazon profit tool, such as Sellerboard',
      shortName: 'an Amazon profit tool',
      summary: 'Sellerboard pulls Amazon fees, refunds, and storage costs, and adds PPC automation, restock planning, and reimbursement claims for tens of dollars a month. Its Shopify product is separate, so it suits brands where Amazon is most of revenue.',
      revisit: 'Revisit when Shopify, TikTok Shop, or wholesale are big enough that you need one profit number across channels.',
      compare: ['sellerboard-vs-custom-data-warehouse'],
      fallbackReasons: ['Amazon fee detail is the hardest part to get right, and Sellerboard keeps that mapping current.', 'It is far cheaper than a warehouse while Amazon is the main channel.'],
    },
    'managed-warehouse': {
      name: 'A packaged ecommerce warehouse, such as Polar or Daasity',
      shortName: 'a packaged warehouse',
      summary: 'Both put modeled commerce data in a warehouse you can query, without a data project of your own. Polar includes a dedicated Snowflake database and a pixel and suits Shopify-led brands; Daasity has prebuilt omnichannel models and retail data connectors.',
      revisit: 'Revisit when your revenue, cost, or channel rules stop fitting the vendor’s predefined model.',
      compare: ['polar-analytics-vs-custom-data-warehouse', 'daasity-vs-custom-data-warehouse', 'glew-vs-custom-data-warehouse', 'saras-analytics-vs-custom-data-warehouse'],
      fallbackReasons: ['You get warehouse access and modeled data without hiring for it.', 'Your answers fit standard ecommerce definitions, which these products maintain for you.'],
    },
    'custom-build': {
      name: 'A warehouse, dbt, and BI build',
      shortName: 'a custom build',
      summary: 'Load every channel into Snowflake or BigQuery, model it in dbt to your own definitions, and report in a BI tool such as Sigma. It costs a project plus warehouse, connector, and BI bills, so it should replace manual work you can name.',
      revisit: 'Check a packaged warehouse such as Daasity or Polar first. If its model fits your rules, it is the cheaper route.',
      compare: ['daasity-vs-custom-data-warehouse', 'polar-analytics-vs-custom-data-warehouse', 'saras-analytics-vs-custom-data-warehouse', 'shopify-analytics-vs-custom-data-warehouse'],
      fallbackReasons: ['Your channels and rules need a model you own.', 'Packaged tools would leave some of your reporting in spreadsheets.'],
      disclosure: 'This is the work Greyfield Data does, so weigh this result with that in mind.',
    },
  },
  questions: [
    {
      id: 'channels',
      label: 'Sales channels',
      text: 'Where do you sell?',
      options: [
        { value: 'shopify', label: 'Shopify only', hint: 'One store, or a few on Shopify.', points: { 'shopify-reports': 3, 'profit-app': 2, 'attribution-tool': 2, 'retention-tool': 2, 'managed-warehouse': 1, 'amazon-tool': -6, 'custom-build': -1 }, reason: 'You sell on Shopify only, so a Shopify-native report or app sees every order.' },
        { value: 'amazon', label: 'Shopify plus Amazon', hint: 'Both are a real share of revenue.', points: { 'shopify-reports': -2, 'attribution-tool': 1, 'retention-tool': 2, 'amazon-tool': 1, 'managed-warehouse': 2, 'custom-build': 2 }, reason: 'You sell on Shopify and Amazon, so the tool needs to read both channels, not just the store.', reasons: { 'retention-tool': 'You sell on Shopify and Amazon, and Peel reads both.' } },
        { value: 'amazon-first', label: 'Mostly Amazon', hint: 'Amazon is most of revenue; a store is secondary.', points: { 'amazon-tool': 6, 'shopify-reports': -4, 'profit-app': -2, 'attribution-tool': -2, 'retention-tool': -1, 'custom-build': 1 }, reason: 'Amazon is most of your business, and Amazon fees are where profit tools differ most.' },
        { value: 'multi', label: 'Plus TikTok Shop, wholesale, or retail', hint: 'Orders arrive from three or more places.', points: { 'custom-build': 4, 'managed-warehouse': 3, 'shopify-reports': -4, 'attribution-tool': -1, 'retention-tool': -1, 'amazon-tool': -2 }, reason: 'You sell beyond Shopify and Amazon, so TikTok Shop, wholesale, or retail orders need to land in one model.' },
      ],
    },
    {
      id: 'revenue',
      label: 'Annual revenue',
      text: 'What is your annual revenue?',
      options: [
        { value: 'under-1m', label: 'Under $1M', points: { 'shopify-reports': 3, 'profit-app': 2, 'amazon-tool': 1, 'managed-warehouse': -2, 'custom-build': -4 }, reason: 'At under $1M a year, a tool you set up in an afternoon is worth more than a project.' },
        { value: '1-10m', label: '$1M to $10M', points: { 'profit-app': 1, 'attribution-tool': 2, 'retention-tool': 2, 'amazon-tool': 1, 'managed-warehouse': 1, 'custom-build': -1 }, reason: 'At $1M to $10M, a focused paid tool usually pays for itself before a data project would.' },
        { value: '10-50m', label: '$10M to $50M', points: { 'managed-warehouse': 2, 'custom-build': 2, 'attribution-tool': 1, 'retention-tool': 1, 'shopify-reports': -1, 'profit-app': -1 }, reason: 'At $10M to $50M, numbers that disagree start to cost more than a proper model.' },
        { value: 'over-50m', label: 'Over $50M', points: { 'custom-build': 4, 'managed-warehouse': 2, 'shopify-reports': -2, 'profit-app': -2, 'amazon-tool': -1 }, reason: 'Above $50M, owning and auditing the data model usually matters more than setup speed.' },
      ],
    },
    {
      id: 'users',
      label: 'Report users',
      text: 'Who will use the reports most?',
      options: [
        { value: 'founders', label: 'Founders, mostly', hint: 'A daily check on sales and spend.', points: { 'shopify-reports': 2, 'profit-app': 2, 'amazon-tool': 1 }, reason: 'The reports are mainly for founders, so a ready-made dashboard beats a flexible one.' },
        { value: 'marketing', label: 'The marketing team', hint: 'Campaigns, creative, and channel spend.', points: { 'attribution-tool': 3, 'retention-tool': 1 }, reason: 'The marketing team is the main audience, and attribution tools are built around their daily questions.' },
        { value: 'finance', label: 'Finance and leadership', hint: 'Numbers that have to tie to the books.', points: { 'custom-build': 3, 'managed-warehouse': 2, 'shopify-reports': -1, 'attribution-tool': -1 }, reason: 'Finance needs numbers that tie to the books, which means reconciled orders, refunds, and fees.' },
        { value: 'ops', label: 'Operations', hint: 'Stock, fulfilment, and purchasing.', points: { 'amazon-tool': 1, 'managed-warehouse': 1, 'custom-build': 1 }, reason: 'Operations needs stock and sales in the same place.' },
      ],
    },
    {
      id: 'margin',
      label: 'Margin after fees',
      text: 'Do you need margin after every fee, not just revenue?',
      options: [
        { value: 'must', label: 'Yes, that is the point', hint: 'Product cost, shipping, payment and marketplace fees, ad spend.', points: { 'profit-app': 2, 'amazon-tool': 2, 'managed-warehouse': 2, 'custom-build': 2, 'shopify-reports': -2, 'attribution-tool': -1 }, reason: 'You need margin after every fee, not just revenue.' },
        { value: 'nice', label: 'It would help', points: { 'profit-app': 1, 'managed-warehouse': 1 }, reason: 'Margin would help, but it is not the main job.' },
        { value: 'no', label: 'Revenue and orders are enough for now', points: { 'shopify-reports': 2, 'attribution-tool': 1, 'retention-tool': 1 }, reason: 'Revenue and orders are enough for now, and Shopify reports those well.' },
      ],
    },
    {
      id: 'definitions',
      label: 'Metric definitions',
      text: 'Do you need your own metric definitions?',
      options: [
        { value: 'custom', label: 'Yes, our own rules', hint: 'How revenue, returns, bundles, or costs are counted.', points: { 'custom-build': 4, 'managed-warehouse': 1, 'shopify-reports': -1, 'profit-app': -1, 'attribution-tool': -1, 'retention-tool': -1, 'amazon-tool': -1 }, reason: 'You want your own rules for revenue, returns, and costs, which packaged tools only partly allow.' },
        { value: 'vendor', label: 'A vendor’s standard definitions are fine', points: { 'shopify-reports': 1, 'profit-app': 1, 'attribution-tool': 1, 'retention-tool': 1, 'amazon-tool': 1, 'managed-warehouse': 1, 'custom-build': -3 }, reason: 'Standard definitions are fine, so there is no need to write your own.' },
      ],
    },
    {
      id: 'team',
      label: 'Data person',
      text: 'Do you have a data person in house?',
      options: [
        { value: 'yes', label: 'Yes, someone who writes SQL', points: { 'custom-build': 3, 'managed-warehouse': 2 }, reason: 'You have someone in house who can own SQL models and keep them correct.' },
        { value: 'partner', label: 'No, but we would use an agency or consultant', points: { 'custom-build': 1, 'managed-warehouse': 1 }, reason: 'You would bring in outside help for data work.' },
        { value: 'no', label: 'No, and we do not plan to', points: { 'shopify-reports': 1, 'profit-app': 1, 'attribution-tool': 1, 'retention-tool': 1, 'amazon-tool': 1, 'custom-build': -2 }, reason: 'No one will own data models, so the tool has to maintain itself.' },
      ],
    },
    {
      id: 'budget',
      label: 'Monthly budget',
      text: 'What can you spend a month on analytics tools and data work?',
      options: [
        { value: 'under-300', label: 'Under $300', points: { 'shopify-reports': 3, 'profit-app': 2, 'amazon-tool': 2, 'managed-warehouse': -3, 'custom-build': -5 }, reason: 'Your budget fits Shopify reports or a low-cost app, not a data project.' },
        { value: '300-1500', label: '$300 to $1,500', points: { 'attribution-tool': 2, 'retention-tool': 2, 'profit-app': 1, 'amazon-tool': 1, 'managed-warehouse': 1, 'custom-build': -2 }, reason: 'Your budget covers a dedicated paid app.' },
        { value: '1500-5000', label: '$1,500 to $5,000', points: { 'managed-warehouse': 3, 'custom-build': 1, 'attribution-tool': 1, 'retention-tool': 1 }, reason: 'Your budget leaves room for a packaged warehouse product.' },
        { value: 'over-5000', label: 'Over $5,000', points: { 'custom-build': 3, 'managed-warehouse': 2 }, reason: 'Your budget can fund a build plus warehouse, connector, and BI bills.' },
      ],
    },
    {
      id: 'focus',
      label: 'Main question',
      text: 'What is the main question you need answered?',
      options: [
        { value: 'attribution', label: 'Which ads and channels are working', points: { 'attribution-tool': 5, 'managed-warehouse': 1 }, reason: 'Your main question is which ads are working, which is what attribution tools are built for.' },
        { value: 'profit', label: 'What we actually make on each order', points: { 'profit-app': 3, 'amazon-tool': 2, 'custom-build': 2, 'managed-warehouse': 1 }, reason: 'Your main question is profit, so the tool has to handle costs and fees, not just revenue.' },
        { value: 'retention', label: 'Who comes back, and what they are worth', points: { 'retention-tool': 5, 'managed-warehouse': 1 }, reason: 'Your main question is retention, and cohort tools answer it out of the box.' },
        { value: 'inventory', label: 'What to reorder, and when', points: { 'custom-build': 3, 'amazon-tool': 2, 'managed-warehouse': 1, 'shopify-reports': 1 }, reason: 'Your main question is inventory across channels, which needs stock and sales from every channel in one model.', reasons: { 'amazon-tool': 'Your main question is inventory, and Sellerboard includes an Amazon restock planner.' } },
      ],
    },
  ],
});

export const BI_QUIZ = Object.freeze({
  id: 'bi',
  path: '/tools/bi-tool-quiz',
  order: ['metabase', 'power-bi', 'tableau', 'looker', 'hex', 'omni', 'sigma'],
  results: {
    metabase: {
      name: 'Metabase',
      shortName: 'Metabase',
      summary: 'Open source BI with a free self-hosted edition. Hard to beat for cheap, simple dashboards and basic customer-facing embeds on a budget.',
      revisit: 'Revisit when business users need a real spreadsheet on large warehouse tables, or to enter data back into the warehouse.',
      compare: ['sigma-vs-metabase'],
      fallbackReasons: ['Metabase keeps cost and setup low.', 'Your answers do not call for features you would pay more for.'],
    },
    'power-bi': {
      name: 'Power BI',
      shortName: 'Power BI',
      summary: 'Microsoft’s BI tool. Hard to beat on price and fit when you run on Microsoft 365, your team knows DAX, and your data is in Fabric or SQL Server.',
      revisit: 'Revisit if you move to Snowflake or Databricks and business users want to work in a spreadsheet on live data.',
      compare: ['sigma-vs-power-bi'],
      fallbackReasons: ['Power BI fits a Microsoft company on price and familiarity.', 'Your answers do not need features Power BI lacks.'],
    },
    tableau: {
      name: 'Tableau',
      shortName: 'Tableau',
      summary: 'The most control over visual design, a self-hosted option, and a strong fit if you run on Salesforce.',
      revisit: 'Revisit if your data moves to Snowflake or Databricks and the people asking questions think in spreadsheets.',
      compare: ['sigma-vs-tableau'],
      fallbackReasons: ['Tableau gives your builders the most control over how dashboards look.', 'Your answers favour polished, designed dashboards.'],
    },
    looker: {
      name: 'Looker',
      shortName: 'Looker',
      summary: 'Every metric defined once in LookML, reviewed in git, and served the same way everywhere. The deepest fit on Google Cloud.',
      revisit: 'Revisit if business users need to explore and build without waiting on a model change, or need to enter data back into the warehouse.',
      compare: ['sigma-vs-looker'],
      fallbackReasons: ['Looker defines each metric once, in code.', 'Your answers put consistency ahead of ad hoc exploration.'],
    },
    hex: {
      name: 'Hex',
      shortName: 'Hex',
      summary: 'Notebooks for analysts and data scientists who work in SQL and Python and publish what they find as apps.',
      revisit: 'Revisit when business users need to explore and maintain reports themselves. Many companies run Hex alongside a BI tool.',
      compare: ['sigma-vs-hex'],
      fallbackReasons: ['Hex is built for the people who will do the work.', 'Your answers point to analysis more than self-serve reporting.'],
    },
    omni: {
      name: 'Omni',
      shortName: 'Omni',
      summary: 'A code-defined semantic model in git, two-way dbt sync, and governed AI over that model.',
      revisit: 'Revisit if finance and operations users want a full spreadsheet on warehouse data and need to enter data back into it.',
      compare: ['sigma-vs-omni'],
      fallbackReasons: ['Omni keeps metric definitions in code your data team controls.', 'Your answers favour a governed model over spreadsheets.'],
    },
    sigma: {
      name: 'Sigma',
      shortName: 'Sigma',
      summary: 'A spreadsheet interface on live warehouse data, with input tables for typing plans and adjustments back into the warehouse. Strongest on Snowflake and Databricks.',
      revisit: 'Check the runner-up as well. If its price or fit is better for your team, it may be the better buy.',
      compare: ['sigma-vs-power-bi', 'sigma-vs-tableau', 'sigma-vs-looker', 'sigma-vs-omni', 'sigma-vs-metabase', 'sigma-vs-hex'],
      fallbackReasons: ['Sigma lets spreadsheet users work on live warehouse data.', 'Your answers favour business users building their own reports.'],
      disclosure: 'Greyfield Data sells Sigma consulting, so weigh this result with that in mind. Sigma is listed last for ties.',
    },
  },
  questions: [
    {
      id: 'warehouse',
      label: 'Warehouse',
      text: 'Where does the data live?',
      options: [
        { value: 'snowflake', label: 'Snowflake', points: { sigma: 3, omni: 3, hex: 2, metabase: 1, tableau: 1 }, reason: 'You run on Snowflake, which {name} queries directly.' },
        { value: 'databricks', label: 'Databricks', points: { sigma: 3, omni: 2, hex: 2, 'power-bi': 1, tableau: 1, looker: 1, metabase: 1 }, reason: 'You run on Databricks, which {name} queries directly.' },
        { value: 'bigquery', label: 'BigQuery', points: { looker: 4, omni: 2, hex: 2, sigma: 2, metabase: 1, tableau: 1 }, reason: 'You run on BigQuery, which {name} queries directly.', reasons: { looker: 'You run on BigQuery, and Looker is Google’s own BI tool with the deepest fit on Google Cloud.' } },
        { value: 'redshift', label: 'Redshift or another cloud warehouse', points: { sigma: 2, omni: 2, hex: 2, metabase: 2, looker: 1, tableau: 1, 'power-bi': 1 }, reason: 'Your data is in a cloud warehouse that {name} can query directly.' },
        { value: 'microsoft', label: 'Microsoft Fabric, Azure SQL, or SQL Server', points: { 'power-bi': 6, tableau: 1 }, reason: 'Your data is in Microsoft Fabric or SQL Server, where Power BI is the native choice.' },
        { value: 'postgres', label: 'A Postgres or MySQL database', points: { metabase: 4, hex: 2, tableau: 1, 'power-bi': 1, omni: 1 }, reason: 'Your data is in Postgres or MySQL, which {name} connects to without a warehouse project.' },
        { value: 'none', label: 'No warehouse yet', hint: 'Spreadsheets and app exports.', points: { 'power-bi': 3, metabase: 2, tableau: 1, sigma: -2, looker: -3, omni: -2, hex: -1 }, reason: 'You have no warehouse yet. Power BI can import files and app data directly, while Sigma, Looker, and Omni need a warehouse first.', reasons: { metabase: 'You have no warehouse yet, and Metabase works on an ordinary app database.' } },
      ],
    },
    {
      id: 'builders',
      label: 'Report builders',
      text: 'Who will build most of the reports?',
      options: [
        { value: 'sql', label: 'Analysts who write SQL', hint: 'A data team that models and publishes.', points: { looker: 3, omni: 3, hex: 2, metabase: 1, sigma: 1 }, reason: 'Analysts write SQL and want models they control, which suits {name}.' },
        { value: 'python', label: 'Analysts in SQL and Python notebooks', hint: 'Exploratory work and data science.', points: { hex: 6, omni: 1 }, reason: 'Your builders work in SQL and Python, which is what Hex notebooks are built for.' },
        { value: 'visual', label: 'Analysts who design polished dashboards', hint: 'Visual design matters to the audience.', points: { tableau: 5, 'power-bi': 1 }, reason: 'Your builders care most about visual design, where Tableau gives the most control.' },
        { value: 'finance', label: 'Finance and ops in spreadsheets', hint: 'Excel or Google Sheets today.', points: { sigma: 5, 'power-bi': 2 }, reason: 'Finance builds the reports in spreadsheets, and Sigma gives them a spreadsheet on live warehouse data.', reasons: { 'power-bi': 'Finance lives in Excel, and Power BI sits next to it in Microsoft 365.' } },
        { value: 'business', label: 'Business users, little SQL', hint: 'Point and click, filters, simple charts.', points: { metabase: 3, sigma: 2, 'power-bi': 2, omni: 1, tableau: 1 }, reason: 'Business users will build their own views, so {name} has to work without SQL.' },
      ],
    },
    {
      id: 'microsoft',
      label: 'Microsoft 365',
      text: 'Is the company a Microsoft 365 shop?',
      options: [
        { value: 'yes', label: 'Yes, Teams, Excel, and SharePoint every day', points: { 'power-bi': 5 }, reason: 'You run on Microsoft 365, and Power BI fits Teams, Excel, SharePoint, and your Microsoft licensing.' },
        { value: 'some', label: 'Partly', points: { 'power-bi': 1 }, reason: 'You use some Microsoft 365.' },
        { value: 'no', label: 'No, Google Workspace or other', points: { 'power-bi': -1 }, reason: 'You are not tied to Microsoft 365.' },
      ],
    },
    {
      id: 'writeback',
      label: 'Write-back',
      text: 'Do people need to type data back in, such as budgets, forecasts, or adjustments?',
      options: [
        { value: 'yes', label: 'Yes, it is a core need', points: { sigma: 5 }, reason: 'You need to type plans or adjustments back into the warehouse, which Sigma does with input tables.' },
        { value: 'nice', label: 'It would be useful', points: { sigma: 2 }, reason: 'Write-back would help, and Sigma has it built in with input tables.' },
        { value: 'no', label: 'No, reports are read-only', points: { metabase: 1, looker: 1, hex: 1, tableau: 1 }, reason: 'Reports are read-only, so write-back is not a factor.' },
      ],
    },
    {
      id: 'embed',
      label: 'Embedded analytics',
      text: 'Do you need to embed dashboards for your customers?',
      options: [
        { value: 'yes', label: 'Yes, in our product or portal', points: { metabase: 2, tableau: 2, looker: 2, sigma: 1, omni: 1, 'power-bi': 1 }, reason: 'You need customer-facing embedded analytics, which {name} supports.', reasons: { metabase: 'You need customer-facing embeds, and Metabase does basic embedding on a budget.' } },
        { value: 'no', label: 'No, internal use only', points: { hex: 1 }, reason: 'Reports are for internal use only.' },
      ],
    },
    {
      id: 'governance',
      label: 'Governance',
      text: 'How tightly do metric definitions need to be governed?',
      options: [
        { value: 'code', label: 'Defined once in code, reviewed in git', points: { looker: 5, omni: 4 }, reason: 'You want every metric defined once in code and reviewed in git, which is how {name} works.' },
        { value: 'certified', label: 'Role-based access and certified data sources', points: { 'power-bi': 2, sigma: 2, tableau: 2, looker: 1, omni: 1 }, reason: 'You need role-based access and certified data sources, which {name} handles well.' },
        { value: 'light', label: 'Light, a small team that trusts each other', points: { metabase: 3, hex: 2 }, reason: 'Governance can stay light, so a simpler tool is enough.' },
      ],
    },
    {
      id: 'budget',
      label: 'Budget',
      text: 'What does the budget look like?',
      options: [
        { value: 'minimal', label: 'As close to free as possible', points: { metabase: 5, 'power-bi': 2, looker: -3, sigma: -2, tableau: -2, omni: -1 }, reason: 'Budget is tight, and Metabase is open source with a free self-hosted edition.', reasons: { 'power-bi': 'Budget is tight, and Power BI per-user licences are low cost, especially next to Microsoft 365.' } },
        { value: 'per-user', label: 'Per-user licences for a small team', points: { 'power-bi': 3, hex: 2, metabase: 1, omni: 1 }, reason: 'A per-user plan for a small team fits your budget.' },
        { value: 'platform', label: 'A platform contract is fine if the fit is right', points: { sigma: 2, looker: 2, tableau: 2, omni: 2, hex: 1 }, reason: 'Budget allows a platform contract, so fit matters more than price.' },
      ],
    },
    {
      id: 'current',
      label: 'Current tool',
      text: 'What do you use today?',
      options: [
        { value: 'none', label: 'Spreadsheets, no BI tool', points: { sigma: 1, metabase: 1, 'power-bi': 1 }, reason: 'You are starting fresh, with no BI tool to migrate from.' },
        { value: 'power-bi', label: 'Power BI', points: { 'power-bi': 3, sigma: 1 }, reason: 'You already run Power BI, and staying avoids a migration.' },
        { value: 'tableau', label: 'Tableau', points: { tableau: 3, sigma: 1 }, reason: 'You already run Tableau, and staying avoids a migration.' },
        { value: 'looker', label: 'Looker', points: { looker: 3, omni: 2 }, reason: 'You already run Looker, and staying avoids a migration.', reasons: { omni: 'Coming from Looker, Omni’s code-defined model will feel familiar.' } },
        { value: 'metabase', label: 'Metabase', points: { metabase: 3, sigma: 1, hex: 1 }, reason: 'You already run Metabase, and staying avoids a migration.' },
        { value: 'other', label: 'Something else', points: {}, reason: 'Your current tool does not tilt the choice.' },
      ],
    },
  ],
});

export const QUIZZES = Object.freeze({ ecommerce: ECOMMERCE_QUIZ, bi: BI_QUIZ });

function own(object, key) {
  return object !== null && typeof object === 'object' && Object.prototype.hasOwnProperty.call(object, key);
}

function findOption(question, value) {
  if (typeof value !== 'string') return null;
  return question.options.find((option) => option.value === value) || null;
}

/** Keep only answers that name a real option of a real question. */
export function cleanAnswers(quiz, answers) {
  const clean = {};
  for (const question of quiz.questions) {
    const value = own(answers, question.id) ? answers[question.id] : undefined;
    if (findOption(question, value)) clean[question.id] = value;
  }
  return clean;
}

function points(option, resultId) {
  const value = own(option.points, resultId) ? Number(option.points[resultId]) : 0;
  return Number.isFinite(value) ? value : 0;
}

/** Score every result for the given answers; ties keep the quiz's `order`. */
export function rank(quiz, answers) {
  const clean = cleanAnswers(quiz, answers);
  const totals = quiz.order.map((id, index) => {
    let total = 0;
    for (const question of quiz.questions) {
      const option = findOption(question, clean[question.id]);
      if (option) total += points(option, id);
    }
    return { id, name: quiz.results[id].name, points: total, index };
  });
  totals.sort((a, b) => b.points - a.points || a.index - b.index);
  return totals.map(({ id, name, points: total }) => ({ id, name, points: total }));
}

function fill(text, result) {
  return text.replaceAll('{name}', result.shortName);
}

export function reasonsFor(quiz, answers, resultId) {
  const clean = cleanAnswers(quiz, answers);
  const result = quiz.results[resultId];
  const found = [];
  quiz.questions.forEach((question, index) => {
    const option = findOption(question, clean[question.id]);
    if (!option) return;
    const value = points(option, resultId);
    if (value < REASON_MIN_POINTS) return;
    const text = own(option.reasons, resultId) ? option.reasons[resultId] : option.reason;
    if (text) found.push({ text: fill(text, result), value, index });
  });
  found.sort((a, b) => b.value - a.value || a.index - b.index);
  const reasons = found.slice(0, MAX_REASONS).map((entry) => entry.text);
  for (const fallback of result.fallbackReasons) {
    if (reasons.length >= 2) break;
    if (!reasons.includes(fallback)) reasons.push(fallback);
  }
  return reasons;
}

export function score(quiz, answers) {
  const clean = cleanAnswers(quiz, answers);
  const answered = Object.keys(clean).length;
  const total = quiz.questions.length;
  const complete = answered === total;
  const ranking = rank(quiz, clean);
  if (!complete) return { answered, total, complete, ranking, result: null };
  const [winner, second] = ranking;
  const result = quiz.results[winner.id];
  const compare = [...result.compare];
  if (quiz.id === 'bi' && second.id !== 'sigma') {
    const extra = `sigma-vs-${second.id}`;
    if (winner.id === 'sigma') compare.splice(0, compare.length, extra);
    else if (!compare.includes(extra)) compare.push(extra);
  }
  return {
    answered,
    total,
    complete,
    ranking,
    result: {
      id: winner.id,
      points: winner.points,
      name: result.name,
      summary: result.summary,
      revisit: result.revisit,
      disclosure: result.disclosure || null,
      reasons: reasonsFor(quiz, clean, winner.id),
      compare,
      runnerUp: { id: second.id, name: quiz.results[second.id].name, summary: quiz.results[second.id].summary, points: second.points },
    },
  };
}

/** Read answers from a URL query string; unknown keys and values are ignored, UTMs included. */
export function answersFromSearch(quiz, search) {
  const params = new URLSearchParams(search || '');
  const raw = {};
  for (const question of quiz.questions) {
    const value = params.get(question.id);
    if (value !== null) raw[question.id] = value;
  }
  return cleanAnswers(quiz, raw);
}

/** Merge answers into an existing query string, keeping other params such as UTMs. */
export function searchWithAnswers(quiz, answers, existing = '') {
  const params = new URLSearchParams(existing || '');
  const clean = cleanAnswers(quiz, answers);
  for (const question of quiz.questions) {
    if (own(clean, question.id)) params.set(question.id, clean[question.id]);
    else params.delete(question.id);
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}
